import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Save, Calculator, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const EditDryingCycleModal = ({ isOpen, onClose, onSave, item, machines, isLoading }) => {
  const [formData, setFormData] = useState({
    fecha: '',
    maquina: null,
    contador: '',
    contadorNuevo: '',
    ciclos: 0,
    capacidad: 0,
    kilosSecados: 0,
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (item) {
      const fechaFormateada = item.fecha
        ? new Date(item.fecha).toISOString().split('T')[0]
        : '';

      setFormData({
        fecha: fechaFormateada,
        maquina: item.maquina || null,
        contador: item.contador || '',
        contadorNuevo: item.contadorNuevo || '',
        ciclos: item.ciclos || 0,
        capacidad: item.capacidad || 0,
        kilosSecados: item.kilosSecados || 0,
      });
    }
  }, [item]);

  useEffect(() => {
    const contador = parseFloat(formData.contador) || 0;
    const contadorNuevo = parseFloat(formData.contadorNuevo) || 0;
    const capacidad = parseFloat(formData.capacidad) || 0;

    if (contadorNuevo > contador) {
      const ciclosCalculados = contadorNuevo - contador;
      const kilosCalculados = ciclosCalculados * capacidad;
      setFormData(prev => ({ ...prev, ciclos: ciclosCalculados, kilosSecados: kilosCalculados }));
    } else if (contadorNuevo !== 0) {
      setFormData(prev => ({ ...prev, ciclos: 0, kilosSecados: 0 }));
    }
  }, [formData.contador, formData.contadorNuevo, formData.capacidad]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleMachineChange = (machineId) => {
    const selectedMachine = machines.find(m => m._id === machineId);
    if (selectedMachine) {
      setFormData(prev => ({
        ...prev,
        maquina: { id: selectedMachine._id, nombre: selectedMachine.nombre },
        capacidad: selectedMachine.capacidad || 0,
      }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.fecha) newErrors.fecha = 'La fecha es requerida';
    if (!formData.maquina || !formData.maquina.id) newErrors.maquina = 'La máquina es requerida';
    if (!formData.contador || formData.contador === '') newErrors.contador = 'El contador anterior es requerido';
    if (!formData.contadorNuevo || formData.contadorNuevo === '') newErrors.contadorNuevo = 'El contador nuevo es requerido';

    const contador = parseFloat(formData.contador) || 0;
    const contadorNuevo = parseFloat(formData.contadorNuevo) || 0;
    if (contadorNuevo <= contador) newErrors.contadorNuevo = 'El contador nuevo debe ser mayor que el anterior';

    const machine = machines.find(m => m._id === formData.maquina?.id);
    if (machine && machine.consumoLimiteDia && formData.ciclos > machine.consumoLimiteDia) {
      newErrors.ciclos = `Los ciclos (${formData.ciclos}) superan el límite diario (${machine.consumoLimiteDia})`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    const [year, month, day] = formData.fecha.split('-');
    const recordDate = new Date(Date.UTC(parseInt(year), parseInt(month) - 1, parseInt(day), 12, 0, 0));

    const dataToSave = {
      fecha: recordDate.toISOString(),
      maquina: formData.maquina,
      contador: parseFloat(formData.contador),
      contadorNuevo: parseFloat(formData.contadorNuevo),
      ciclos: formData.ciclos,
      capacidad: formData.capacidad,
      kilosSecados: formData.kilosSecados,
      lavanderia: item.lavanderia,
    };

    onSave(dataToSave);
  };

  if (!isOpen) return null;

  const machine = machines.find(m => m._id === formData.maquina?.id);
  const exceedsLimit = machine?.consumoLimiteDia && formData.ciclos > machine.consumoLimiteDia;
  const nearLimit = machine?.consumoLimiteDia && formData.ciclos > machine.consumoLimiteDia * 0.8 && formData.ciclos <= machine.consumoLimiteDia;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/50"
            onClick={onClose}
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative z-10 w-full max-w-2xl mx-4"
          >
            <Card className="shadow-2xl">
              <div className="flex items-center justify-between p-6 border-b">
                <div>
                  <h2 className="text-2xl font-bold">Editar Registro de Ciclos de Secado</h2>
                  <p className="text-sm text-muted-foreground mt-1">{item?.maquina?.nombre}</p>
                </div>
                <Button variant="ghost" size="icon" onClick={onClose} disabled={isLoading}>
                  <X className="h-5 w-5" />
                </Button>
              </div>

              <form onSubmit={handleSubmit}>
                <CardContent className="p-6 space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="fecha">
                      Fecha del registro <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="fecha"
                      type="date"
                      value={formData.fecha}
                      onChange={(e) => handleChange('fecha', e.target.value)}
                      max={new Date().toISOString().split('T')[0]}
                      className={errors.fecha ? 'border-red-500' : ''}
                    />
                    {errors.fecha && <p className="text-sm text-red-500">{errors.fecha}</p>}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="maquina">
                      Máquina <span className="text-red-500">*</span>
                    </Label>
                    <Select value={formData.maquina?.id || ''} onValueChange={handleMachineChange}>
                      <SelectTrigger className={errors.maquina ? 'border-red-500' : ''}>
                        <SelectValue placeholder="Seleccionar máquina" />
                      </SelectTrigger>
                      <SelectContent>
                        {machines.map((machine) => (
                          <SelectItem key={machine._id} value={machine._id}>
                            {machine.nombre} - {machine.capacidad} kg
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.maquina && <p className="text-sm text-red-500">{errors.maquina}</p>}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="contador">
                        Contador Anterior <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="contador"
                        type="number"
                        step="1"
                        value={formData.contador}
                        onChange={(e) => handleChange('contador', e.target.value)}
                        placeholder="0"
                        className={errors.contador ? 'border-red-500' : ''}
                      />
                      {errors.contador && <p className="text-sm text-red-500">{errors.contador}</p>}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="contadorNuevo">
                        Contador Nuevo <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="contadorNuevo"
                        type="number"
                        step="1"
                        value={formData.contadorNuevo}
                        onChange={(e) => handleChange('contadorNuevo', e.target.value)}
                        placeholder="0"
                        className={errors.contadorNuevo ? 'border-red-500' : ''}
                      />
                      {errors.contadorNuevo && <p className="text-sm text-red-500">{errors.contadorNuevo}</p>}
                    </div>
                  </div>

                  {formData.maquina && (
                    <div className="p-4 bg-muted rounded-lg">
                      <p className="text-sm text-muted-foreground">Capacidad de la máquina</p>
                      <p className="font-medium text-lg">{formData.capacidad} kg</p>
                    </div>
                  )}

                  {machine?.consumoLimiteDia && (
                    <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-md">
                      <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-500" />
                      <span className="text-xs text-amber-700 dark:text-amber-400 font-medium">
                        Límite diario: {machine.consumoLimiteDia} ciclos
                      </span>
                    </div>
                  )}

                  {formData.contador && formData.contadorNuevo && parseFloat(formData.contadorNuevo) > parseFloat(formData.contador) && (
                    <div className={`p-4 rounded-lg space-y-3 ${
                      exceedsLimit ? 'bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800' :
                      nearLimit ? 'bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800' :
                      'bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800'
                    }`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Calculator className={`h-5 w-5 ${
                            exceedsLimit ? 'text-red-600' : nearLimit ? 'text-amber-600' : 'text-blue-600'
                          }`} />
                          <span className="font-semibold">Cálculos Automáticos</span>
                        </div>
                        {exceedsLimit && <AlertTriangle className="h-5 w-5 text-red-600" />}
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-muted-foreground">Ciclos Completados</p>
                          <p className={`text-lg font-bold ${
                            exceedsLimit ? 'text-red-700 dark:text-red-400' :
                            nearLimit ? 'text-amber-700 dark:text-amber-400' :
                            'text-blue-700 dark:text-blue-400'
                          }`}>
                            {formData.ciclos}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Kilos Secados</p>
                          <p className={`text-lg font-bold ${
                            exceedsLimit ? 'text-red-700 dark:text-red-400' :
                            nearLimit ? 'text-amber-700 dark:text-amber-400' :
                            'text-blue-700 dark:text-blue-400'
                          }`}>
                            {formData.kilosSecados.toFixed(0)} kg
                          </p>
                        </div>
                      </div>

                      {exceedsLimit && (
                        <p className="text-sm text-red-600 dark:text-red-400 font-medium flex items-center gap-1">
                          <AlertTriangle className="h-4 w-4" />
                          Supera el límite diario configurado
                        </p>
                      )}
                      {nearLimit && (
                        <p className="text-sm text-amber-600 dark:text-amber-400 font-medium">
                          Cerca del límite diario
                        </p>
                      )}
                      {errors.ciclos && (
                        <p className="text-sm text-red-600 dark:text-red-400 font-medium">{errors.ciclos}</p>
                      )}
                    </div>
                  )}
                </CardContent>

                <div className="flex justify-end gap-3 p-6 border-t bg-muted/50">
                  <Button type="button" variant="outline" onClick={onClose} disabled={isLoading}>
                    Cancelar
                  </Button>
                  <Button type="submit" disabled={isLoading || Object.keys(errors).length > 0}>
                    {isLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                        Guardando...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Guardar Cambios
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </Card>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default EditDryingCycleModal;
